import React, { Component, ErrorInfo, ReactNode } from "react";
import { Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Layout } from "./components/Layout";
import { Company } from "./types";
import { api, findCompanyBySlug, getCompanyModifiedTime } from "./lib/api";
import { companies as localCompanies } from "./data/companies";

import { TooltipProvider } from "./components/ui/tooltip";
import { Toaster, toast } from "sonner";
import { Dashboard } from "./components/Dashboard";
import { CompanyDetail } from "./components/CompanyDetail";
import { Skeleton } from "./components/ui/skeleton";

// Code splitting secondary pages to optimize mobile PageSpeed Lighthouse scores
const About = React.lazy(() => import("./components/About").then(m => ({ default: m.About })));
const Sponsor = React.lazy(() => import("./components/Sponsor").then(m => ({ default: m.Sponsor })));
const Report = React.lazy(() => import("./components/Report").then(m => ({ default: m.Report })));
const Contact = React.lazy(() => import("./components/Contact").then(m => ({ default: m.Contact })));
const SitemapDirectory = React.lazy(() => import("./components/SitemapDirectory").then(m => ({ default: m.SitemapDirectory })));
const Terms = React.lazy(() => import("./components/Terms").then(m => ({ default: m.Terms })));
const Privacy = React.lazy(() => import("./components/Privacy").then(m => ({ default: m.Privacy })));

const makeBreadcrumbSchema = (items: { name: string; item: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((it, idx) => ({
    "@type": "ListItem",
    "position": idx + 1,
    "name": it.name,
    "item": it.item
  }))
});

const siteNavigationSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SiteNavigationElement",
      "name": "Corporate Directory",
      "url": "https://biofail.com/directory",
      "description": "Interactive alphabetical registry of all monitored corporations, carbon metrics, and ESG auditing."
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Methodology & About",
      "url": "https://biofail.com/about",
      "description": "A detailed review of our telemetry analysis, carbon intensity calculations, and satellite-driven methodology."
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Anonymous Whistleblower Portal",
      "url": "https://biofail.com/report",
      "description": "Secure endpoint to submit case files, report direct emissions conspiracies, or dump corporate violations."
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Sponsors & Partners",
      "url": "https://biofail.com/sponsor",
      "description": "Fund independent surveillance infrastructure, whistleblower resources, and environmental investigations."
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Contact Corporate Watchdogs",
      "url": "https://biofail.com/contact",
      "description": "Administrative support, researcher dataset licensing, and corrections team ingress."
    }
  ]
};

const websiteSearchSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Biofail Index",
  "url": "https://biofail.com",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://biofail.com/?search={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Biofail Index",
  "url": "https://biofail.com",
  "logo": "https://biofail.com/logo.png",
  "description": "An independent corporate surveillance and environmental auditing coalition tracking global corporate environmental impact, human rights violations, and carbon emissions.",
  "knowsAbout": [
    "Corporate environmental auditing",
    "ESG risk assessment",
    "Carbon emissions tracking",
    "Deforestation monitoring",
    "Human rights advocacy",
    "Whistleblower reporting"
  ],
  "funder": {
    "@type": "Organization",
    "name": "The Green Sentinel Foundation",
    "url": "https://biofail.com/sponsor"
  }
};

// Error Boundary for mission-critical reliability
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("MISSION_CRITICAL_FAILURE:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-stone-950 text-stone-50 p-6 font-mono">
          <div className="max-w-xl w-full space-y-6 border border-red-900/50 p-8 bg-black">
            <h1 className="text-red-500 text-2xl font-bold flex items-center gap-3">
              <span className="animate-pulse">●</span> SYSTEM_HALT
            </h1>
            <p className="text-stone-400 text-sm leading-relaxed">
              The Biofail Index internal rendering engine has encountered an unrecoverable exception. This dossier is currently inaccessible.
            </p>
            <div className="p-4 bg-red-950/20 border border-red-900/30 text-red-400 text-xs overflow-auto">
              {this.state.error?.toString()}
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-red-600 text-white font-bold text-sm hover:bg-red-500 transition-colors"
            >
              REBOOT SYSTEM
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wrapper component to handle company detail resolution from slug
function CompanyDetailWrapper({ 
  onReport, 
  onSelectCompany 
}: { 
  onReport: (name: string) => void,
  onSelectCompany: (c: Company | number) => void
}) {
  const { slug } = useParams<{ slug: string }>();
  
  // Synchronously initialize the company details from local dataset to prevent unmount on mount loop
  const [company, setCompany] = React.useState<Company | null>(() => {
    if (slug) {
      return findCompanyBySlug(localCompanies, slug);
    }
    return null;
  });
  
  const [loading, setLoading] = React.useState(() => {
    if (slug) {
      return !findCompanyBySlug(localCompanies, slug);
    }
    return true;
  });
  
  const navigate = useNavigate();

  React.useEffect(() => {
    if (slug) {
      const hasLocal = !!findCompanyBySlug(localCompanies, slug);
      if (!hasLocal) {
        setLoading(true);
      }
      api.getCompany(slug)
        .then(res => {
          if (res) {
            setCompany(res);
          } else {
            if (!hasLocal) navigate("/");
          }
        })
        .catch((err) => {
          console.error("CompanyDetail: Fetch failed", err);
          if (!hasLocal) navigate("/");
        })
        .finally(() => setLoading(false));
    }
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl space-y-8 animate-pulse">
        {/* Back navigation skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skeleton className="w-4 h-4 bg-stone-200" />
            <Skeleton className="h-4 w-28 bg-stone-200" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-36 bg-stone-200" />
            <Skeleton className="h-5 w-24 bg-stone-150 rounded-sm" />
          </div>
        </div>

        {/* Header Block skeleton */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border border-stone-200 p-4 rounded-sm bg-white shadow-sm">
          <div className="flex flex-col md:flex-row items-center gap-5 flex-1 w-full">
            <Skeleton className="w-20 h-20 rounded-sm bg-stone-250 shrink-0" />
            <div className="space-y-2 text-center md:text-left w-full max-w-sm">
              <div className="flex items-center justify-center md:justify-start gap-2">
                <Skeleton className="h-6 w-48 bg-stone-250" />
                <Skeleton className="h-4 w-16 bg-stone-200 rounded-sm" />
              </div>
              <div className="flex items-center justify-center md:justify-start gap-2">
                <Skeleton className="h-4.5 w-64 bg-stone-200" />
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4 w-full md:w-auto justify-center md:justify-end border-t md:border-t-0 border-stone-100 pt-3 md:pt-0 shrink-0">
            <div className="text-center md:text-right px-4 border-r border-stone-200 space-y-1">
              <Skeleton className="h-3 w-16 bg-stone-200" />
              <Skeleton className="h-8 w-14 bg-stone-250" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="w-8 h-8 rounded bg-stone-200" />
              <Skeleton className="w-8 h-8 rounded bg-stone-200" />
            </div>
            <Skeleton className="h-10 w-32 bg-stone-250 rounded-sm" />
          </div>
        </div>

        {/* Primary Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm space-y-3">
              <div className="flex justify-between items-start">
                <Skeleton className="h-3.5 w-16 bg-stone-200" />
                <Skeleton className="w-4 h-4 bg-stone-200 rounded-full" />
              </div>
              <Skeleton className="h-7 w-20 bg-stone-250" />
            </div>
          ))}
        </div>

        {/* Estimated Damages Card */}
        <div className="border border-stone-200 shadow-sm bg-white rounded-sm overflow-hidden p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-stone-100 pb-4">
            <div className="flex items-center gap-2">
              <Skeleton className="w-4 h-4 bg-stone-200" />
              <Skeleton className="h-4.5 w-64 bg-stone-250" />
            </div>
            <Skeleton className="h-5 w-32 bg-stone-150 rounded-sm" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <Skeleton className="h-3 w-48 bg-stone-200" />
              <Skeleton className="h-8 w-60 bg-stone-250" />
              <div className="space-y-2">
                <Skeleton className="h-3.5 w-full bg-stone-200" />
                <Skeleton className="h-3.5 w-5/6 bg-stone-200" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-3 w-32 bg-stone-200" />
                <Skeleton className="h-2 w-full bg-stone-100 animate-pulse" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-40 bg-stone-200" />
                <Skeleton className="h-2 w-full bg-stone-100 animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Side-by-Side Charts & Impact placeholders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="border border-stone-200 shadow-sm bg-white rounded-sm p-6 space-y-4">
            <div className="flex justify-between border-b border-stone-100 pb-3">
              <Skeleton className="h-4.5 w-32 bg-stone-250" />
              <Skeleton className="h-3.5 w-24 bg-stone-200" />
            </div>
            <Skeleton className="h-[180px] w-full bg-stone-100 rounded" />
          </div>

          <div className="border border-stone-200 shadow-sm bg-white rounded-sm p-6 space-y-6">
            <div className="flex justify-between border-b border-stone-100 pb-3">
              <Skeleton className="h-4.5 w-40 bg-stone-250" />
              <Skeleton className="h-4 w-12 bg-stone-200" />
            </div>
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex justify-between">
                    <Skeleton className="h-3 w-24 bg-stone-200" />
                    <Skeleton className="h-3 w-10 bg-stone-250" />
                  </div>
                  <Skeleton className="h-3 w-full bg-stone-100 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!company) return null;

  const title = `${company.companyName} Environmental Violations & Emissions Index | Biofail`;
  const desc = `${company.companyName} environmental impact investigation dossier. Track direct Scope 1/2 emissions (${company.totalEmissions || 0} Megatonnes), deforestation footprint (${company.deforestationHectares || 0} hectares cleared), litigation violations index, and climate opposition spending.`;
  
  const bestSlug = company.companyName
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim() || company.slug;

  const canonicalUrl = `https://biofail.com/${bestSlug}`;

  const companySchema = {
    "@context": "https://schema.org",
    "@type": "Corporation",
    "name": company.companyName,
    "url": `https://biofail.com/${bestSlug}`,
    "logo": `https://biofail.com/logos/${company.slug}.png`,
    "sector": company.sector,
    "region": company.region,
    "description": company.description || `${company.companyName} is tracked under our emissions index for direct satellite deforestation HA and corporate action.`,
    ...(company.foundedYear ? { "foundingDate": company.foundedYear.toString() } : {}),
    "knowsAbout": [
      "Environmental Protection",
      "Emissions Auditing",
      "Climate Lobbying Reduction",
      "Global Warming Mitigation"
    ],
    "identifier": {
      "@type": "PropertyValue",
      "name": "Biofail Destruction Evil Score Index",
      "value": `${company.evilScore}/100`
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": company.headquarters || "Global Headquarters"
    }
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta name="robots" content="index, follow" />
        <meta property="article:modified_time" content={getCompanyModifiedTime(company)} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(companySchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(makeBreadcrumbSchema([
            { name: "Biofail Index", item: "https://biofail.com" },
            { name: "Directory", item: "https://biofail.com/directory" },
            { name: company.companyName, item: `https://biofail.com/${bestSlug}` }
          ]))}
        </script>
      </Helmet>
      <CompanyDetail 
        company={company} 
        onBack={() => navigate("/")} 
        onReport={onReport}
        onSelectCompany={onSelectCompany}
      />
    </>
  );
}

// Legacy client-side URL redirect fallback for SEO and user bookmarks
function LegacyCompanyRedirect() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate(slug ? `/${slug}` : "/directory", { replace: true });
  }, [slug, navigate]);
  return null;
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = React.useState("");
  const [siteVerificationToken, setSiteVerificationToken] = React.useState("");
  
  React.useEffect(() => {
    const fetchVerificationToken = async () => {
      try {
        const { doc, getDoc } = await import("firebase/firestore");
        const { db } = await import("./lib/firebase");
        const docRef = doc(db, "seo", "config");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data && data.googleSiteVerification) {
            setSiteVerificationToken(data.googleSiteVerification);
            console.log("[SEO] Loaded Google verification token client-side:", data.googleSiteVerification);
          }
        }
      } catch (e) {
        console.warn("[SEO] Verification token fetch failed, skipping:", e);
      }
    };
    fetchVerificationToken();
  }, []);
  
  // Initialize states with local datasets statically compiled into the bundle
  const [allCompanies, setAllCompanies] = React.useState<Company[]>(() => {
    return localCompanies;
  });
  
  const [globalStats, setGlobalStats] = React.useState<any>(() => {
    return {
      totalCompanies: localCompanies.length,
      criticalThreats: localCompanies.filter(c => (c.evilScore || 0) >= 90).length,
      totalViolations: localCompanies.reduce((acc, c) => acc + (c.violations || 0), 0),
      totalDeforestation: localCompanies.reduce((acc, c) => acc + (c.deforestationHectares || 0), 0)
    };
  });
  
  const [isDataLoading, setIsDataLoading] = React.useState(false);
  
  const [activeCompany, setActiveCompany] = React.useState<Company | null>(() => {
    const path = window.location.pathname;
    const parts = path.split("/").filter(Boolean);
    if (parts.length === 1 && !["about", "sponsor", "report", "methodology", "contact", "partners", "terms", "privacy"].includes(parts[0])) {
      const slug = parts[0];
      return findCompanyBySlug(localCompanies, slug);
    }
    return null;
  });

  // Determine activeSection from path
  const getActiveSection = () => {
    const path = location.pathname;
    if (path === "/") return "index";
    if (!["/about", "/sponsor", "/report", "/methodology", "/contact", "/partners", "/terms", "/privacy"].includes(path)) return "detail";
    return path.substring(1);
  };

  const activeSection = getActiveSection();

  const fetchData = React.useCallback(async (silent = false) => {
    if (!silent) setIsDataLoading(true);
    try {
      console.log("App: Synchronizing with intelligence database...");
      const isHomepage = window.location.pathname === "/";

      const results = await Promise.allSettled([
        api.getStats(),
        isHomepage ? api.getCompanies() : Promise.resolve([])
      ]);

      if (results[0].status === "fulfilled") {
        setGlobalStats(results[0].value);
      } else {
        console.warn("App: Stats synchronization failed", (results[0] as any).reason);
      }

      if (results[1].status === "fulfilled" && results[1].value.length > 0) {
        setAllCompanies(results[1].value);
        
        // Sync active company from URL if needed
        const path = window.location.pathname;
        const parts = path.split("/").filter(Boolean);
        if (parts.length === 1 && !["about", "sponsor", "report", "methodology", "contact", "partners", "terms", "privacy"].includes(parts[0])) {
          const slug = parts[0];
          const company = findCompanyBySlug(results[1].value, slug);
          if (company) setActiveCompany(company);
        }
      } else {
        console.warn("App: Companies synchronization failed", (results[1] as any).reason);
      }

      if (results[0].status === "rejected" && (!isHomepage || results[1].status === "rejected")) {
        throw new Error("Critical synchronization failure across database nodes.");
      }
    } catch (error) {
      console.error("App: Data fetch fatal error:", error);
    } finally {
      if (!silent) setIsDataLoading(false);
    }
  }, []);

  React.useEffect(() => {
    // Run the initial load silently to prevent clearing local data and blinking!
    fetchData(true);
  }, [fetchData]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("search");
    if (query !== null && query !== search) {
      setSearch(query);
    }
  }, [location.search, search]);

  React.useEffect(() => {
    const interval = setInterval(() => fetchData(true), 45000); // 45s sync
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSelectCompany = async (companyOrId: Company | number) => {
    if (typeof companyOrId === "number") {
      if (companyOrId === -1) {
        navigate("/sponsor");
        return;
      }
      try {
        const company = await api.getCompany(companyOrId);
        if (company) {
          setActiveCompany(company);
          if (company.slug) navigate(`/${company.slug}`);
        }
      } catch (e) {
        console.error("SelectCompany failed:", e);
      }
    } else if (companyOrId.slug) {
      setActiveCompany(companyOrId);
      navigate(`/${companyOrId.slug}`);
    }
    window.scrollTo(0, 0);
  };

  const handleReportFromDetail = async (companyName: string) => {
    let found = allCompanies.find(
      c => c.companyName.toLowerCase() === companyName.toLowerCase()
    );
    if (!found) {
      try {
        const { companies } = await import("./data/companies");
        found = companies.find(
          c => c.companyName.toLowerCase() === companyName.toLowerCase()
        );
      } catch (e) {
        console.error("Lazy load static companies failed", e);
      }
    }
    if (found) {
      setActiveCompany(found);
    } else {
      setActiveCompany({ companyName } as Company);
    }
    navigate(`/report?company=${encodeURIComponent(companyName)}`);
  };

  return (
    <TooltipProvider>
      <Toaster position="top-center" richColors />
      {siteVerificationToken && (
        <Helmet>
          <meta name="google-site-verification" content={siteVerificationToken} />
        </Helmet>
      )}
      <div className="bg-stone-50 min-h-screen">
        <Layout 
          activeSection={activeSection} 
          setActiveSection={(s) => navigate(s === "index" ? "/" : `/${s}`)}
          search={search}
          setSearch={setSearch}
          onSelectCompany={handleSelectCompany}
          selectedCompany={activeCompany}
          stats={globalStats}
        >
          <React.Suspense fallback={
            <div className="w-full max-w-7xl mx-auto px-6 mt-6 space-y-6 animate-pulse">
              {/* Header line skeletons */}
              <div className="space-y-2">
                <Skeleton className="h-7 w-64 bg-stone-250" />
                <Skeleton className="h-4 w-96 bg-stone-200" />
              </div>
              
              {/* Metrics bar skeletons */}
              <div className="flex flex-wrap gap-4 p-4 border border-stone-200 rounded bg-white shadow-sm">
                <Skeleton className="h-5 w-24 bg-stone-200" />
                <Skeleton className="h-5 w-24 bg-stone-200" />
                <Skeleton className="h-5 w-32 bg-stone-200" />
                <Skeleton className="h-5 w-36 bg-stone-200" />
              </div>

              {/* Card blocks skeletons */}
              <div className="border border-stone-200 rounded bg-white shadow-sm p-6 space-y-6">
                <div className="flex justify-between items-center pb-4 border-b">
                  <Skeleton className="h-5 w-32 bg-stone-250" />
                  <Skeleton className="h-8 w-48 bg-stone-200" />
                </div>
                <div className="space-y-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <div className="flex items-center gap-3 w-full max-w-md">
                        <Skeleton className="w-8 h-8 rounded-full bg-stone-200" />
                        <Skeleton className="h-4 w-full bg-stone-250" />
                      </div>
                      <Skeleton className="h-4 w-12 bg-stone-200" />
                      <Skeleton className="h-4 w-16 bg-stone-200" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          }>
            <Routes>
              <Route path="/" element={
                <>
                  <Helmet>
                    <title>Biofail Index | The Corporate Destruction Index</title>
                    <meta name="description" content="The definitive real-time database of global corporate environmental impact, human rights violations, and carbon emissions tracking." />
                    <link rel="canonical" href="https://biofail.com" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" }
                      ]))}
                    </script>
                    <script type="application/ld+json">
                      {JSON.stringify(siteNavigationSchema)}
                    </script>
                    <script type="application/ld+json">
                      {JSON.stringify(websiteSearchSchema)}
                    </script>
                    <script type="application/ld+json">
                      {JSON.stringify(organizationSchema)}
                    </script>
                  </Helmet>
                  <Dashboard 
                    onSelectCompany={handleSelectCompany} 
                    search={search} 
                    setSearch={setSearch} 
                    stats={globalStats} 
                    initialCompanies={allCompanies}
                    isLoading={isDataLoading}
                  />
                </>
              } />
              <Route path="/about" element={
                <>
                  <Helmet>
                    <title>Telemetry & Methodology | Biofail Index</title>
                    <meta name="description" content="Learn about the ESG risk algorithms, satellite flaring indicators, deforestation metrics, and aggregate corporate tracking pipelines." />
                    <link rel="canonical" href="https://biofail.com/about" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" },
                        { name: "About", item: "https://biofail.com/about" }
                      ]))}
                    </script>
                  </Helmet>
                  <About setActiveSection={(s) => { setActiveCompany(null); navigate(s === "index" ? "/" : `/${s}`); }} stats={globalStats} />
                </>
              } />
              <Route path="/sponsor" element={
                <>
                  <Helmet>
                    <title>Partners & Supporters | Biofail Index</title>
                    <meta name="description" content="Support independent corporate auditing. Fund whistleblower infrastructures, surveillance algorithms, and climate watches." />
                    <link rel="canonical" href="https://biofail.com/sponsor" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" },
                        { name: "Sponsor", item: "https://biofail.com/sponsor" }
                      ]))}
                    </script>
                  </Helmet>
                  <Sponsor stats={globalStats} />
                </>
              } />
              <Route path="/partners" element={
                <>
                  <Helmet>
                    <title>Partners & Supporters | Biofail Index</title>
                    <meta name="description" content="Support independent corporate auditing. Fund whistleblower infrastructures, surveillance algorithms, and climate watches." />
                    <link rel="canonical" href="https://biofail.com/partners" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" },
                        { name: "Partners", item: "https://biofail.com/partners" }
                      ]))}
                    </script>
                  </Helmet>
                  <Sponsor stats={globalStats} />
                </>
              } />
              <Route path="/directory" element={
                <>
                  <Helmet>
                    <title>A-Z Corporate Environmental Directory | Biofail Registry</title>
                    <meta name="description" content="Complete interactive alphabetical catalog of all tracked corporations, structured for instant search, carbon metrics tracking, and ESG auditing." />
                    <link rel="canonical" href="https://biofail.com/directory" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" },
                        { name: "Directory", item: "https://biofail.com/directory" }
                      ]))}
                    </script>
                  </Helmet>
                  <SitemapDirectory />
                </>
              } />
              <Route path="/report" element={
                <>
                  <Helmet>
                    <title>Crypt Whistleblower Portal | Submit Case File</title>
                    <meta name="description" content="Securely report corporate environmental infractions, illegal toxic dumping, emissions conspiracies, and human rights violations completely anonymously." />
                    <link rel="canonical" href="https://biofail.com/report" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" },
                        { name: "Report", item: "https://biofail.com/report" }
                      ]))}
                    </script>
                  </Helmet>
                  <Report />
                </>
              } />
              <Route path="/contact" element={
                <>
                  <Helmet>
                    <title>Contact Corporate Watchdogs | Biofail Terminal</title>
                    <meta name="description" content="Transmit direct administrative inbounds, research reviews, dataset inquiries, or corrections." />
                    <link rel="canonical" href="https://biofail.com/contact" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" },
                        { name: "Contact", item: "https://biofail.com/contact" }
                      ]))}
                    </script>
                  </Helmet>
                  <Contact />
                </>
              } />
              <Route path="/terms" element={
                <>
                  <Helmet>
                    <title>Terms of Service & Regulatory Disclaimers | Biofail Index</title>
                    <meta name="description" content="Legal framework, liability limits, and database disclaimer declarations for the Biofail Registry." />
                    <link rel="canonical" href="https://biofail.com/terms" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" },
                        { name: "Terms of Service", item: "https://biofail.com/terms" }
                      ]))}
                    </script>
                  </Helmet>
                  <Terms />
                </>
              } />
              <Route path="/privacy" element={
                <>
                  <Helmet>
                    <title>Privacy Policy & Data Security | Biofail Index</title>
                    <meta name="description" content="Confidentiality parameters, minimalist web tracking, and user data protection protocols of the Biofail Registry." />
                    <link rel="canonical" href="https://biofail.com/privacy" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" },
                        { name: "Privacy Policy", item: "https://biofail.com/privacy" }
                      ]))}
                    </script>
                  </Helmet>
                  <Privacy />
                </>
              } />
              <Route path="/methodology" element={
                <>
                  <Helmet>
                    <title>Detailed Audits Methodology | Biofail Index</title>
                    <meta name="description" content="Deep technical guide to telemetry calculation structures, carbon intensity factors, and direct corporate compliance metrics." />
                    <link rel="canonical" href="https://biofail.com/methodology" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" },
                        { name: "Methodology", item: "https://biofail.com/methodology" }
                      ]))}
                    </script>
                  </Helmet>
                  <About setActiveSection={(s) => { setActiveCompany(null); navigate(s === "index" ? "/" : `/${s}`); }} initialScrollTo="methodology" />
                </>
              } />
              <Route path="/company/:slug" element={<LegacyCompanyRedirect />} />
              <Route path="/company" element={<LegacyCompanyRedirect />} />
              <Route path="/:slug" element={
                <CompanyDetailWrapper 
                  onReport={handleReportFromDetail} 
                  onSelectCompany={handleSelectCompany} 
                />
              } />
              <Route path="*" element={
                <>
                  <Helmet>
                    <title>Biofail Index | The Corporate Destruction Index</title>
                    <meta name="description" content="The definitive real-time database of global corporate environmental impact, human rights violations, and carbon emissions tracking." />
                    <link rel="canonical" href="https://biofail.com" />
                    <script type="application/ld+json">
                      {JSON.stringify(makeBreadcrumbSchema([
                        { name: "Biofail Index", item: "https://biofail.com" }
                      ]))}
                    </script>
                    <script type="application/ld+json">
                      {JSON.stringify(siteNavigationSchema)}
                    </script>
                  </Helmet>
                  <Dashboard 
                    onSelectCompany={handleSelectCompany} 
                    search={search} 
                    setSearch={setSearch} 
                    initialCompanies={allCompanies}
                    isLoading={isDataLoading}
                  />
                </>
              } />
            </Routes>
          </React.Suspense>
        </Layout>
      </div>
    </TooltipProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}
